import { ReactNode, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface OverlaySectionProps {
  /** Section title - subtle, not loud */
  title?: string;
  /** Optional description below title */
  description?: string;
  /** Section content */
  children: ReactNode;
  /** Whether section is collapsible (default: false) */
  collapsible?: boolean;
  /** Default open state for collapsible sections (default: true) */
  defaultOpen?: boolean;
  /** Optional icon to display before title */
  icon?: ReactNode;
  /** Additional className for the section container */
  className?: string;
  /** Whether to show a subtle top border (default: false, uses spacing instead) */
  showBorder?: boolean;
}

/**
 * OverlaySection - Semantic section primitive for overlay bodies
 * 
 * Design principles (from Overlay Contract):
 * - Sections are separated by spacing, not borders
 * - Headings are subtle, not loud
 * - White space as structure, not boxes inside boxes
 * - Each section answers one question
 * 
 * Usage:
 * <OverlaySection title="Order Summary">
 *   <SummaryContent />
 * </OverlaySection>
 * 
 * <OverlaySection title="Notes" collapsible defaultOpen={false}>
 *   <NotesContent />
 * </OverlaySection>
 */
export function OverlaySection({
  title,
  description,
  children,
  collapsible = false,
  defaultOpen = true,
  icon,
  className,
  showBorder = false,
}: OverlaySectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const headerContent = (
    <div className="flex items-center gap-2">
      {icon && <span className="text-muted-foreground">{icon}</span>}
      <div>
        {title && (
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
        )}
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );

  const sectionClasses = cn(
    "space-y-3",
    showBorder && "pt-4 sm:pt-6 border-t border-border/50",
    !showBorder && "pt-4 sm:pt-6 first:pt-0",
    className
  );

  if (collapsible) {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className={sectionClasses}>
        <CollapsibleTrigger className="flex items-center justify-between w-full group px-2 py-1.5 -mx-2 rounded-md hover:bg-muted/50 transition-colors">
          {headerContent}
          <ChevronDown 
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200",
              isOpen && "rotate-180"
            )} 
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3">
          {children}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <section className={sectionClasses}>
      {(title || description) && (
        <div className="pb-1">{headerContent}</div>
      )}
      {children}
    </section>
  );
}

/**
 * OverlaySummaryRow - Key-value pair for summary sections
 * 
 * Usage:
 * <OverlaySummaryRow label="Status">Paid</OverlaySummaryRow>
 * <OverlaySummaryRow label="Customer" muted>John Doe</OverlaySummaryRow>
 */
interface OverlaySummaryRowProps {
  label: string;
  children: ReactNode;
  /** Whether value should be muted (default: false) */
  muted?: boolean;
  /** Additional className */
  className?: string;
}

export function OverlaySummaryRow({
  label,
  children,
  muted = false,
  className
}: OverlaySummaryRowProps) {
  return (
    <div className={cn("flex items-start justify-between gap-4 py-1.5", className)}>
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className={cn(
        "text-sm font-medium text-right min-w-0 break-words",
        muted ? "text-muted-foreground" : "text-foreground"
      )}>
        {children}
      </span>
    </div>
  );
}

/**
 * OverlaySectionGroup - Container for multiple sections with consistent spacing
 * 
 * Usage:
 * <OverlaySectionGroup>
 *   <OverlaySection title="Summary">...</OverlaySection>
 *   <OverlaySection title="Items">...</OverlaySection>
 * </OverlaySectionGroup>
 */
interface OverlaySectionGroupProps {
  children: ReactNode;
  className?: string;
}

export function OverlaySectionGroup({ children, className }: OverlaySectionGroupProps) {
  return (
    <div className={cn("divide-y-0 space-y-0", className)}>
      {children}
    </div>
  );
}
